/* =============================================================================
 * Lightweight i18n
 * -----------------------------------------------------------------------------
 * English strings ARE the keys — the code stays readable and English needs no
 * dictionary. Other languages map the exact English string to a translation.
 * Unknown strings fall back to English, so partially translated screens never
 * break.
 *
 * USAGE (components — re-renders on language change):
 *   const t = useT();
 *   <h1>{t('Purchase Orders')}</h1>
 *   toast.success(t('Saved'));
 *   t('Welcome, {name}', { name: user.firstName })
 *
 * USAGE (module scope, outside React — e.g. constants consumed at render time):
 *   Keep the constant in English and translate where it is rendered:
 *   <span>{t(item.label)}</span>
 * ========================================================================== */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import id from './id';

const DICTIONARIES = { id };

/** Languages with a full UI translation. Everything else falls back to English. */
export const TRANSLATED_LANGUAGES = ['en', 'id'];

const normalize = (code) => {
  const c = (code || 'en').toLowerCase().split('-')[0];
  return c === 'en' || DICTIONARIES[c] ? c : 'en';
};

// Module-level current language so plain (non-hook) call sites work too.
let currentLang = 'en';

const interpolate = (str, vars) => {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] === undefined || vars[k] === null ? m : String(vars[k])));
};

/**
 * Translate an English string. Safe to call anywhere (falls back to English),
 * but inside components prefer `useT()` so the UI re-renders on language change.
 */
export function t(text, vars) {
  if (text === null || text === undefined) return text;
  const dict = DICTIONARIES[currentLang];
  const out = (dict && dict[text]) || text;
  return interpolate(out, vars);
}

const I18nContext = createContext({ lang: 'en', setLang: () => {}, t });

export function I18nProvider({ language, children }) {
  const [lang, setLangState] = useState(() => normalize(language));

  // Follow the authenticated user's saved preference.
  useEffect(() => {
    const next = normalize(language);
    currentLang = next;
    setLangState(next);
  }, [language]);

  const setLang = useCallback((code) => {
    const next = normalize(code);
    currentLang = next;
    setLangState(next);
  }, []);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Returns the translate function; subscribing components re-render on language change. */
export function useT() {
  return useContext(I18nContext).t;
}

/** Current language + setter (setter is for live preview, e.g. the onboarding tour). */
export function useI18n() {
  return useContext(I18nContext);
}
